import PropTypes from 'prop-types';
import React from 'react';

import BudgetLineItemsTableRow from './BudgetLineItemsTableRow';
import {
  isEditingItemSettlementLineItem,
  settlementLineItemToEdit,
} from '../../../shared/settlementRowEditing';

const ARTIST_PAYMENTS_SECTION = "Artist Payments";

function paymentLabel(item) {
  return `Less ${item.name && item.name.length > 0 ? item.name : "Payment"}`;
}

const ArtistPaymentRows = ({
  items,
  remainingDue,
  artistName,
  isSettling,
  requestFormat,
  showCurrencyConversion,
  globalCurrency,
  formatCurrency,
  numberWithCommas,
  csrfToken,
  team,
  offer,
  offerId,
  isV3,
  itemToEdit,
  itemToEditChanged,
  editedDropCountChanged,
  createSettlementLineItem,
  deleteSettlementLineItem,
  deleteSettlementOverride,
  setBudgetState
}) => {
  const isEditable = isSettling && requestFormat === "html";
  const shownItems = isEditable ? items : items.filter((item) => item.calculatedTotal > 0);

  const blankCell = <td style={{"padding": "3px 0px"}}><span>&nbsp;</span></td>;

  return (
    <>
      {shownItems.map((item) => (
        isEditable && isEditingItemSettlementLineItem(item, itemToEdit) ? (
          <BudgetLineItemsTableRow key={item.overridable_id}
                                   title={ARTIST_PAYMENTS_SECTION}
                                   formatCurrency={formatCurrency}
                                   numberWithCommas={numberWithCommas}
                                   csrfToken={csrfToken}
                                   team={team}
                                   offer={offer}
                                   isSettling={isSettling}
                                   requestFormat={requestFormat}
                                   itemToEdit={itemToEdit}
                                   itemToEditChanged={itemToEditChanged}
                                   editedDropCountChanged={editedDropCountChanged}
                                   deleteSettlementLineItem={deleteSettlementLineItem}
                                   deleteSettlementOverride={deleteSettlementOverride}
                                   offerId={offerId}
                                   setBudgetState={setBudgetState}
                                   isV3={isV3}
                                   showCurrencyConversion={showCurrencyConversion}
                                   globalCurrency={globalCurrency}
                                   showQuantityInput={false}
                                   namePlaceholder="Payment, e.g. Cash"
                                   amountPlaceholder="Amount"
                                   item={item} />
        ) : (
          <tr key={item.overridable_id} data-testid="artist-payment-row">
            <td style={{"padding": "3px 9px 3px 15px"}}>
              {paymentLabel(item)}
            </td>
            {blankCell}
            {blankCell}
            {showCurrencyConversion ? (
              <td style={{"padding": "3px 9px 3px 0px", "textAlign": "right"}}>
                {globalCurrency.formatted(item.calculatedTotal)}
              </td>
            ) : null}
            <td style={{"padding": "3px 15px 3px 0px", "textAlign": "right"}}>
              <strong>
                {formatCurrency(item.calculatedTotal)}
              </strong>
            </td>
            <td style={{"textAlign": "center", "padding": "3px 0px"}}>
              {isEditable ? (
                <a href="#"
                   className="text-muted"
                   title="Edit"
                   data-testid="artist-payment-edit"
                   onClick={
                     (e) => {
                       e.preventDefault();

                       itemToEditChanged(settlementLineItemToEdit(item));
                       editedDropCountChanged(null);
                     }
                   }>
                  <i aria-hidden className="fas fa-pencil"></i>
                </a>
              ) : (
                <span>&nbsp;</span>
              )}
            </td>
          </tr>
        )
      ))}
      {shownItems.length > 0 ? (
        <tr style={{"fontSize": "16px"}} data-testid="artist-payment-remaining-due">
          <td style={{"padding": "3px 9px 3px 15px"}}>
            <strong>
              {`Remaining Due ${artistName}`.trim()}
            </strong>
          </td>
          {blankCell}
          {blankCell}
          {showCurrencyConversion ? (
            <td style={{"padding": "3px 9px 3px 0px", "textAlign": "right"}}>
              {globalCurrency.formatted(remainingDue)}
            </td>
          ) : null}
          <td style={{"padding": "3px 15px 3px 0px", "textAlign": "right"}}>
            <strong>
              {formatCurrency(remainingDue)}
            </strong>
          </td>
          {blankCell}
        </tr>
      ) : null}
      {isEditable ? (
        <tr>
          <td style={{"padding": "3px 9px 3px 15px"}}>
            <a href="#"
               data-testid="artist-payment-add"
               style={{
                 "textDecoration": "none",
                 "color": "var(--od-c-1982c4, #1982C4)",
                 "fontSize": "12px"
               }}
               onClick={
                 (e) => {
                   e.preventDefault();

                   createSettlementLineItem(csrfToken, team, {
                     offer_id: offerId,
                     section: ARTIST_PAYMENTS_SECTION,
                     quantity: 1
                   });
                   editedDropCountChanged(null);
                 }
               }>
              <strong>
                <i className="fas fa-plus-circle"
                   aria-hidden
                   style={{
                     "marginRight": "10px"
                   }}></i>
                Add Payment
              </strong>
            </a>
          </td>
          {blankCell}
          {blankCell}
          {showCurrencyConversion ? blankCell : null}
          {blankCell}
          {blankCell}
        </tr>
      ) : null}
    </>
  );
};

ArtistPaymentRows.propTypes = {
  items: PropTypes.array.isRequired,
  remainingDue: PropTypes.number.isRequired,
  artistName: PropTypes.string,
  isSettling: PropTypes.bool,
  requestFormat: PropTypes.string,
  showCurrencyConversion: PropTypes.bool,
  globalCurrency: PropTypes.object,
  formatCurrency: PropTypes.func.isRequired,
  numberWithCommas: PropTypes.func.isRequired,
  csrfToken: PropTypes.string,
  team: PropTypes.object.isRequired,
  offer: PropTypes.object,
  offerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isV3: PropTypes.bool,
  itemToEdit: PropTypes.object,
  itemToEditChanged: PropTypes.func.isRequired,
  editedDropCountChanged: PropTypes.func.isRequired,
  createSettlementLineItem: PropTypes.func.isRequired,
  deleteSettlementLineItem: PropTypes.func.isRequired,
  deleteSettlementOverride: PropTypes.func.isRequired,
  setBudgetState: PropTypes.func.isRequired
};

export default ArtistPaymentRows;
